import {
  Action,
  ConfirmModal,
  Icon,
  Menu,
  Modal,
  RelativeDateTime,
  SwitchInput,
} from 'corso-admin-ui';
import { useState } from 'react';
import {
  useAutomationDelete,
  useAutomationUpsert,
  useNextPriorityByHook,
  useToggleAutomation,
} from '~/features/settings/hooks/useAutomations';
import { useStoreId } from '~/providers/StoreUserProvider';

import { automationsDisplayName, Pegasus } from 'corso-types';
import { useLocation } from 'react-router-dom';
import { AutomationEventDisplay } from '~/features/settings/automations/eventDisplay/AutomationEventDisplay';
import {
  ShowAs,
  useAutomationsShowAs,
} from '~/features/settings/automations/hooks/useAutomationsShowAs';
import { Automation } from '~/types';

function AutomationDetailModal({
  automation,
  onClose,
  show,
}: {
  automation: Automation;
  onClose: () => void;
  show: boolean;
}) {
  const description =
    automationsDisplayName.eventType[automation.rule.event.type];
  return (
    <Modal
      onClose={onClose}
      show={show}
      title={automation.name}
      description={
        <div className="flex flex-col gap-1">
          <p className="text-sm text-neutral-400 italic">{description}</p>
          <p className="text-sm text-neutral-400 italic">
            Created <RelativeDateTime dateTime={automation.createdOn} />
          </p>
        </div>
      }
      size="lg"
    >
      <AutomationEventDisplay automation={automation} />
    </Modal>
  );
}

function useAutomationDuplicate({ automation }: { automation: Automation }) {
  const { mutate: upsertAutomation } = useAutomationUpsert();
  const nextPriority = useNextPriorityByHook(automation.hook);

  return () => {
    const { id, rule, ...newRule } = automation;

    const { event } = rule;

    upsertAutomation({
      ...newRule,
      isEnabled: false,
      name: `${automation.name} (Copy)`,
      rule: {
        ...rule,
        priority: nextPriority,
        event: {
          ...event,
          // ! ensure we clear the storeRuleId when duplicating
          storeRuleId: undefined,
        },
      },
    });
  };
}

const flowHooks: readonly Pegasus.Hook[] = Pegasus.returnOrWarrantyHook;
function useShowViewInFlow(automation: Automation) {
  const [showAs] = useAutomationsShowAs();
  return flowHooks.includes(automation.hook) && showAs !== ShowAs.Flow;
}

export default function AutomationActions({
  automation,
}: {
  automation: Automation;
}) {
  const location = useLocation();
  const storeId = useStoreId();
  const toggleRule = useToggleAutomation();
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const { mutate: automationDelete, isPending } = useAutomationDelete();
  const handleDuplicate = useAutomationDuplicate({ automation });

  const [showDetail, setShowDetail] = useState(false);
  const showInFlow = useShowViewInFlow(automation);

  return (
    <div className="flex items-center gap-2">
      <Menu
        align="end"
        buttonAs={
          <Action
            icon="overflowMenu"
            accessibilityLabel={`Actions for ${automation.name} Automation`}
            variant="ghost"
          />
        }
      >
        {showInFlow && (
          <Menu.ItemLink
            to={{
              pathname: `/${storeId}/settings/automations`,
              hash: `store-rule-${automation.id}`,
              search: new URLSearchParams({
                as: ShowAs.Flow,
              }).toString(),
            }}
          >
            <Icon icon="branch" />
            View In Flow
          </Menu.ItemLink>
        )}

        <Menu.ItemLink
          to={{
            pathname: `/${storeId}/settings/automations/edit/${automation.id}`,
            search: new URLSearchParams({
              returnTo: location.pathname,
            }).toString(),
          }}
        >
          <Icon icon="edit" />
          Edit
        </Menu.ItemLink>

        <Menu.ItemButton onClick={handleDuplicate}>
          <Icon icon="duplicate" />
          Duplicate
        </Menu.ItemButton>

        <Menu.ItemButton onClick={() => setShowConfirmDelete(true)}>
          <Icon icon="remove" />
          Delete
        </Menu.ItemButton>
      </Menu>
      <Action
        accessibilityLabel={`View ${automation.name} Rule`}
        icon="eye"
        variant="ghost"
        onClick={() => setShowDetail(true)}
      />

      <AutomationDetailModal
        automation={automation}
        onClose={() => setShowDetail(false)}
        show={showDetail}
      />

      <ConfirmModal
        show={showConfirmDelete}
        loading={isPending}
        title="Delete Rule"
        prompt={`Are you sure you want to delete "${automation.name}"?`}
        cancelText="Cancel"
        confirmText="Delete"
        onCancel={() => setShowConfirmDelete(false)}
        onConfirm={() => {
          automationDelete(automation.id);
          setShowConfirmDelete(false);
        }}
      />

      <SwitchInput
        id="isRuleEnabled"
        label={`${automation.name} Enabled`}
        labelVisuallyHidden
        checked={automation.isEnabled}
        onChange={() => toggleRule(automation)}
      />
    </div>
  );
}
